import type { Combatant, EncounterState } from "@lancer-vtt/domain";
import { StatusEditor, type EncounterDispatch } from "../StatusEditor";
import { SavedRulingLibrary } from "../SavedRulingLibrary";
import { NameField, StatField } from "./fields";
import { ConcealToggle, DisclosureControl, DoorToggle } from "./toggles";
import { ControlPanel, type ControlPanelSlot } from "./ControlPanel";
import { TokenImageControl } from "./TokenImageControl";

/**
 * The **EDIT** inspector body — edits one unit's *definition* on the
 * EDIT desktop: its name ({@link SetCombatantNameCommand `set_combatant_name`}), its board token image
 * (`set_token_image`, {@link TokenImageControl}), its frozen base
 * stats (`edit_combatant_base` — the one sanctioned base mutation, GM-only; HP/heat caps + armor +
 * evasion), and deletion (`remove_combatant`). Everything routes through **existing** commands (no
 * new command was needed — `set_combatant_name` already exists and is permission-gated). Bound to a
 * specific combatant (the per-unit inspector double-click opened, point 4); a null
 * combatant (just deleted, before the window auto-closes) shows a placeholder. The PLAY desktop's
 * inspector is {@link InspectorBody} instead. Edits flow through the host's logged dispatch so they
 * land in the LOG window's timeline.
 */
export function EditInspectorBody({
  combatant: c,
  state,
  actorId,
  slots,
  disabled,
  dispatch,
}: {
  combatant: Combatant | null;
  /** The live encounter state — threaded through only for `StatusEditor`'s source-combatant picker. */
  state: EncounterState;
  actorId: string;
  /** Every slot in the game (GM + players) — the {@link ControlPanel} rows' source. */
  slots: ControlPanelSlot[];
  disabled: boolean;
  // `EncounterDispatch` (`=> Result<void>`) so the status authoring section (`StatusEditor`) can read
  // the reducer's accept/reject; the base/name/delete fields ignore the return, which is fine.
  dispatch: EncounterDispatch;
}) {
  if (!c) {
    return <div className="gms-content gms-content-message">This unit is no longer in the encounter.</div>;
  }
  const off = disabled || !actorId;
  // A base edit replaces the whole frozen base (replace-semantics — `edit_combatant_base`); spread the
  // current base and override the one field so the rest of the stat block is preserved verbatim.
  const editBase = (patch: Partial<Combatant["base"]>) =>
    dispatch({ type: "edit_combatant_base", actorId, combatantId: c.id, base: { ...c.base, ...patch } });

  return (
    // Brutalist re-skin (combat-edit-inspector-brutalist) — the EDIT definition editor wears the same
    // `.npc-ctl-*` control surface the PLAY NpcInspectorBody/MechInspectorBody do, edge-to-edge ink-
    // divided bands. Behaviour is untouched: every field still dispatches its existing command.
    <div className="gms-content npc-ctl">
      <div className="npc-ctl-id">
        <div className="npc-ctl-id-main">
          <span className="npc-ctl-name">{c.name}</span>
          <span className="npc-ctl-classrole">{c.kind} · definition</span>
        </div>
        {/* Fog-of-war conceal toggle + the DISCLOSURE segmented control
            . The EDIT desktop only exists for a GM
            viewer (the host forces PLAY for players), so no extra role gate is needed here — `off`
            mirrors the same tools-disabled thread the other EDIT controls use. */}
        <div className="npc-ctl-id-side">
          <DisclosureControl combatant={c} actorId={off ? "" : actorId} dispatch={dispatch} />
          <ConcealToggle combatant={c} actorId={off ? "" : actorId} dispatch={dispatch} />
        </div>
      </div>

      <NameField
        name={c.name}
        disabled={off}
        onCommit={(name) => dispatch({ type: "set_combatant_name", actorId, combatantId: c.id, name })}
      />

      <TokenImageControl combatant={c} encounterId={state.id} actorId={off ? "" : actorId} dispatch={dispatch} />

      <div className="npc-ctl-edit-field">
        <span className="npc-ctl-label">Base stats</span>
        <div className="npc-ctl-edit-stats">
          <StatField label="Max HP" value={c.base.maxHp} min={1} disabled={off} onCommit={(v) => editBase({ maxHp: v })} />
          {c.base.maxHeat !== undefined && (
            <StatField label="Max heat" value={c.base.maxHeat} min={0} disabled={off} onCommit={(v) => editBase({ maxHeat: v })} />
          )}
          <StatField label="Armor" value={c.base.armor} min={0} disabled={off} onCommit={(v) => editBase({ armor: v })} />
          <StatField label="Evasion" value={c.base.evasion} min={0} disabled={off} onCommit={(v) => editBase({ evasion: v })} />
        </div>
      </div>

      {/* The GM adds/removes statuses here with the shared free-text StatusEditor; the PLAY unit
          sheet mounts the same add form under its chips for the GM. StatusEditor keeps its
          own (shared-with-the-generic-PLAY-body) styling — it's not part of this re-skin's scope. */}
      <div className="npc-ctl-edit-field">
        <StatusEditor combatant={c} state={state} actorId={off ? null : actorId} dispatch={dispatch} canRule />
        {/* GM-only OPEN/SHUT toggle — a no-op render for any non-door combatant
            (the component itself gates on `objectProfile.door`). The EDIT desktop is GM-only at the
            host clamp (players never reach it), so no extra role gate is needed here — `off` mirrors
            the same tools-disabled thread the other EDIT controls use. */}
        <DoorToggle combatant={c} actorId={off ? null : actorId} dispatch={dispatch} />
      </div>

      {/* Who drives and who sees this unit — owner of record + grants.
          GM-only by construction, like every other control on this desktop. */}
      <ControlPanel combatant={c} actorId={off ? "" : actorId} slots={slots} dispatch={dispatch} />

      {/* The GM's saved-ruling LIBRARY MANAGER (2.3) — list/rename/delete,
          deliberately not a composer (authoring is the PLAY palette's KEEP affordance). It sits
          on EDIT because management belongs where authoring belongs, off the PLAY inspector's
          five-second clock. Game-scoped, not combatant-scoped: it takes no `combatant` because a
          saved ruling belongs to the GM's library, not to whichever unit is selected. */}
      <SavedRulingLibrary disabled={off} />

      <div className="npc-ctl-edit-actions">
        <button
          type="button"
          onClick={() => dispatch({ type: "remove_combatant", actorId, combatantId: c.id })}
          disabled={off}
          title={`Delete ${c.name} from the encounter`}
          className="npc-ctl-edit-delete"
        >
          ✕ Delete unit
        </button>
      </div>
    </div>
  );
}
